import React, { useState, useEffect } from 'react';
import KrysConsole from './KrysConsole';
import { motion } from 'framer-motion';
import { ShamayimToggleSwitch } from './ui/shamayim-toggle-switch';

const SplineSection: React.FC = () => {
  const [experienceMode, setExperienceMode] = useState(() => window.location.hash === '#krys-console');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Introduction Screen Component
  const IntroScreen = () => (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden py-24 sm:py-16"
      style={{backgroundColor: '#1C1C1C'}}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* Main Content */}
      <div className="relative text-center px-4 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8 leading-relaxed"
             style={{fontFamily: 'Vixa-Regular, sans-serif'}}>
            "Welcome to Krys's Daily — meet my AI self inside a playful 3D console."
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white mb-12" 
              style={{fontFamily: 'Vixa-Regular, sans-serif'}}>
            Console: <span className="text-[#BBB]">OFF</span>
          </h1>
        </motion.div>

        {/* Activation Control Area */}
        <motion.div
          className="flex flex-col items-center space-y-8"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="flex items-center space-x-6 rounded-2xl px-8 py-6 border border-[#FFFFFF15]" style={{ background: 'linear-gradient(145deg, #2D2D2D, #3C3C3C)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)' }}>
            <span className="text-xl text-[#BBB] font-semibold">Ready to meet my AI self?</span>
            <div className="transform scale-150" style={experienceMode ? { filter: 'drop-shadow(0 0 12px rgba(255, 214, 0, 0.6)) drop-shadow(0 0 24px rgba(255, 214, 0, 0.3))' } : {}}>
              <ShamayimToggleSwitch
                defaultState={false}
                onChange={(isOn) => setExperienceMode(isOn)}
                pattern="hex"
                iconOff="#777"
                iconOn="#FFD600"
                trackBg="linear-gradient(145deg, #555, #777)"
                buttonBg="linear-gradient(145deg, #FFD600, #FFF200)"
                buttonBeforeBg="linear-gradient(145deg, #FFD600, #FFF200)"
              />
            </div>
          </div>
          
          <motion.p 
            className="text-sm leading-relaxed text-[#F1EFE0]/70 max-w-md mx-auto"
          >
            Flip the switch to enter. Press the yellow button to chat about my projects,
            interests, and ideas — press it again to turn the screen off.
            Tap the question-mark boxes for a surprise.
          </motion.p>
          
          {isMobile && (
            <motion.p
              className="text-xs text-gray-400 mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              Chat in your language — my AI self will reply in the same language.
            </motion.p>
          )}
        </motion.div>
      </div>
    </motion.div>
  );

  return (
    <section className="min-h-screen relative">
      {experienceMode ? <KrysConsole onExit={() => setExperienceMode(false)} /> : <IntroScreen />}
    </section>
  );
};

export default SplineSection;
